import { ArrowRight, Lock } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import type { OtcSellPair, OtcSellQuote } from "../../types/cryptoSell.types";
import { roundsToZero, amountPrecision } from "../../utils/cryptoSwapFormat";
import { AssetAmount } from "../shared/AssetAmount";
import { AssetAvatar } from "../shared/CryptoSwapBits";
import { NoticePanel, TicketRow, type TicketNotice } from "../swap/QuoteTicket";
import { QuoteTimerBar } from "../swap/QuoteTimerBar";

export interface SellQuoteTicketProps {
  pair: OtcSellPair;
  quote: OtcSellQuote | null;
  isPricing: boolean;
  clockOffsetMs: number;
  networkLabel: string;
  notice: TicketNotice | null;
  blockMessage: string | null;
  canReview: boolean;
  onReview: () => void;
  onRequote: () => void;
  className?: string;
}

/** The sale as a receipt: what reaches the COP wallet on top, how it gets there below the tear line. */
export function SellQuoteTicket({
  pair,
  quote,
  isPricing,
  clockOffsetMs,
  networkLabel,
  notice,
  blockMessage,
  canReview,
  onReview,
  onRequote,
  className,
}: SellQuoteTicketProps) {
  const { t } = useTranslations("cryptoSwaps");
  const format = useAmountFormatter(pair);
  const dimmed = isPricing || notice?.kind === "expired";
  const feeShown = !!quote && !roundsToZero(quote.fee.total, amountPrecision(quote.fee.currency, pair).max);

  return (
    <aside aria-label={t("quote_ticket_title")} className={cn("flex flex-col gap-3", className)}>
      <div className="overflow-hidden rounded-3xl border border-border shadow-[var(--shadow-elev)]">
        <div className="dark bg-ticket text-ticket-foreground">
          <div className="flex flex-col gap-5 p-6">
            <QuoteTimerBar
              key={quote?.id ?? "none"}
              label={t("quote_ticket_title")}
              quote={quote}
              isPricing={isPricing}
              clockOffsetMs={clockOffsetMs}
            />

            <div className={cn("flex flex-col gap-1.5 transition-opacity", dimmed && "opacity-60")}>
              <span className="text-[13px] text-muted-foreground">{t("sell_ticket_net")}</span>
              <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <AssetAvatar asset={pair.receive_currency} size="md" />
                {quote ? (
                  <AssetAmount
                    value={quote.receive.amount}
                    currency={quote.receive.currency}
                    pair={pair}
                    className="text-[44px] leading-none font-semibold tracking-tight"
                  />
                ) : (
                  <span className="font-mono text-[44px] leading-none font-semibold text-muted-foreground">
                    —
                  </span>
                )}
                <span className="font-mono text-base font-medium text-success-container-foreground">
                  {pair.receive_currency}
                </span>
              </span>
            </div>

            {notice && <NoticePanel notice={notice} onRequote={onRequote} />}
          </div>

          <div aria-hidden="true" className="mx-6 border-t border-dashed border-border" />

          <div className="flex flex-col gap-5 p-6">
            <dl
              className={cn(
                "m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-[13px] transition-opacity",
                dimmed && "opacity-60",
              )}
            >
              <TicketRow label={t("quote_summary_price")} mono={!!quote}>
                {quote
                  ? t("price_line", {
                      asset: quote.send.currency,
                      price: format(quote.price.value, quote.price.currency),
                      currency: quote.price.currency,
                    })
                  : "—"}
              </TicketRow>
              <TicketRow label={t("sell_ticket_send")} mono={!!quote}>
                {quote ? `${format(quote.send.amount, quote.send.currency)} ${quote.send.currency}` : "—"}
              </TicketRow>
              <TicketRow label={t("sell_ticket_proceeds")} mono={!!quote}>
                {quote
                  ? `${format(quote.proceeds.amount, quote.proceeds.currency)} ${quote.proceeds.currency}`
                  : "—"}
              </TicketRow>
              <TicketRow label={t("sell_ticket_fee")} mono={feeShown}>
                {!quote
                  ? "—"
                  : feeShown
                    ? `${format(quote.fee.total, quote.fee.currency)} ${quote.fee.currency}`
                    : t("sell_ticket_fee_none")}
              </TicketRow>
              <TicketRow label={t("quote_summary_network")}>{networkLabel || "—"}</TicketRow>
            </dl>

            <div className="flex flex-col gap-2">
              <ColocaButton
                type="button"
                variant="primary"
                size="lg"
                iconTrailing={ArrowRight}
                className="h-12 w-full"
                disabled={!canReview}
                onClick={onReview}
              >
                {t("sell_submit")}
              </ColocaButton>
              {blockMessage && !notice && (
                <p className="text-center text-xs leading-relaxed text-muted-foreground">{blockMessage}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <p className="flex gap-2 px-1 text-xs leading-relaxed text-muted-foreground">
        <Lock aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
        {t("sell_submit_footnote")}
      </p>
    </aside>
  );
}
